/**
 * ConsentBanner Component
 *
 * Bottom-left card asking whether the visitor is happy to be counted in
 * Google Analytics. Analytics stays off unless "Allow" is clicked, so the
 * banner is only rendered when a decision is still outstanding.
 */

import { motion, AnimatePresence } from 'framer-motion'

export interface ConsentBannerProps {
  /** Whether the banner is visible (i.e. no decision has been made yet) */
  visible: boolean

  /** Callback when the visitor allows analytics */
  onAccept: () => void

  /** Callback when the visitor declines analytics */
  onDecline: () => void

  /** Test ID for E2E testing */
  testId?: string | undefined
}

export function ConsentBanner({
  visible,
  onAccept,
  onDecline,
  testId,
}: ConsentBannerProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 sm:max-w-sm z-50"
          data-testid={testId}
          role="dialog"
          aria-label="Analytics consent"
        >
          <div className="rounded-xl border border-border bg-surface/95 backdrop-blur-xl p-4 shadow-xl">
            <p className="text-sm font-semibold text-foreground mb-1">
              Analytics cookies
            </p>
            <p className="text-sm text-foreground-secondary leading-5">
              We&rsquo;d like to use Google Analytics to see which features get
              used. Your settings files are never uploaded either way.
            </p>

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={onAccept}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-accent-primary text-white hover:bg-accent-primary-hover transition-colors shadow-sm"
              >
                Allow
              </button>
              <button
                onClick={onDecline}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-foreground-secondary hover:text-foreground hover:bg-bg-secondary transition-colors"
              >
                No thanks
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
